import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';
import CommonOptionFields from './CommonOptionFields';

const DisneyThemeParkPackageForm = ({ onSave, onCancel, initialData = {} }) => {
  const [formData, setFormData] = useState({
    title: 'Disney World Resort Package',
    resort_name: '',
    room_type: '',
    view: '',
    check_in_date: '',
    check_out_date: '',
    ticket_type: '5-Day Ticket',
    ticket_options: 'Park Hopper Option',
    ticket_validity_start: '',
    ticket_validity_end: '',
    dining_plan: 'No Dining Plan',
    memory_maker: 'No',
    ground_transportation: '',
    travel_protection: 'Not Included',
    travel_protection_price: 0,
    total_price: 0,
    total_price_due_by: '',
    deposit_due: 0,
    deposit_due_by: '',
    special_requests: '',
    children: [],
    isOption: false,
    optionDescription: '',
    agentNotes: '',
    agentCommission: null,
    photos: [],
    documents: [],
    ...initialData
  });

  const [errors, setErrors] = useState({});

  // Disney World Resort Properties
  const resortOptions = [
    // Deluxe Resorts
    "Disney's Wilderness Lodge",
    "Disney's Contemporary Resort", 
    "Disney's Grand Floridian Resort",
    "Disney's Polynesian Village Resort",
    "Disney's Animal Kingdom Lodge",
    "Disney's Beach Club Resort",
    "Disney's Yacht Club Resort",
    "Disney's BoardWalk Inn",
    "Disney's Lakeshore Lodge",
    // Deluxe Villa Resorts
    "The Villas at Disney's Grand Floridian Resort",
    "Bay Lake Tower at Disney's Contemporary Resort",
    "Disney's Polynesian Villas & Bungalows",
    "Boulder Ridge Villas at Disney's Wilderness Lodge",
    "Copper Creek Villas & Cabins at Disney's Wilderness Lodge",
    "Disney's Animal Kingdom Lodge – Jambo House",
    "Disney's Animal Kingdom Lodge – Kidani Village",
    "Disney's Beach Club Villas",
    "Disney's BoardWalk Villas",
    "Disney's Old Key West Villas",
    "Disney's Riviera Resort",
    "Disney's Saratoga Springs Villas",
    // Moderate Resorts
    "Disney's Caribbean Beach Resort",
    "Disney's Coronado Springs Resort",
    "Disney's Port Orleans Resort - French Quarter",
    "Disney's Port Orleans Resort - Riverside",
    "The Cabins at Disney's Fort Wilderness Resort",
    // Value Resorts
    "Disney's All-Star Movies Resort",
    "Disney's All-Star Music Resort",
    "Disney's All-Star Sports Resort",
    "Disney's Art of Animation Resort",
    "Disney's Pop Century Resort",
    "The Campsites at Disney's Fort Wilderness Resort"
  ];

  const ticketTypes = [
    '2-Day Ticket', '3-Day Ticket', '4-Day Ticket', '5-Day Ticket',
    '6-Day Ticket', '7-Day Ticket', '8-Day Ticket', '9-Day Ticket', '10-Day Ticket'
  ];

  const ticketOptionsChoices = [
    '1 Park Per Day',
    'Park Hopper Option',
    'Water Park and Sports Option', 
    'Park Hopper Plus Option'
  ];

  const diningPlanOptions = [
    'No Dining Plan',
    'Disney Quick-Service Dining Plan',
    'Disney Dining Plan'
  ];


  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleChildrenChange = (count) => {
    const children = Array.from({ length: count }, (_, i) => 
      formData.children[i] || { age: 5 }
    );
    setFormData(prev => ({ ...prev, children }));
  };

  const handleChildAgeChange = (index, age) => {
    const updatedChildren = [...formData.children];
    updatedChildren[index] = { age: parseInt(age) };
    setFormData(prev => ({ ...prev, children: updatedChildren }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.check_in_date) newErrors.check_in_date = 'Check-in date is required';
    if (!formData.check_out_date) newErrors.check_out_date = 'Check-out date is required';
    if (!formData.resort_name) newErrors.resort_name = 'Resort selection is required';
    if (!formData.total_price) newErrors.total_price = 'Total price is required';
    if (!formData.deposit_due) newErrors.deposit_due = 'Deposit amount is required';

    // Date validation
    if (formData.check_in_date && formData.check_out_date) {
      const checkIn = new Date(formData.check_in_date);
      const checkOut = new Date(formData.check_out_date);
      if (checkOut <= checkIn) {
        newErrors.check_out_date = 'Check-out date must be after check-in date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Disney form submitted with data:', formData);
    console.log('Form validation result:', validateForm());
    
    if (validateForm()) {
      const componentData = {
        type: 'disney-wdw-resort-package',
        title: formData.title,
        price: formData.total_price,
        isOption: formData.isOption,
        optionDescription: formData.optionDescription,
        paymentTiming: formData.paymentTiming,
        optionalDepositAmount: formData.optionalDepositAmount,
        agentNotes: formData.agentNotes,
        agentCommission: formData.agentCommission,
        photos: formData.photos,
        documents: formData.documents,
        data: {
          resort_name: formData.resort_name,
          room_type: formData.room_type,
          view: formData.view,
          check_in_date: formData.check_in_date,
          check_out_date: formData.check_out_date,
          ticket_type: formData.ticket_type,
          ticket_options: formData.ticket_options,
          ticket_validity_start: formData.ticket_validity_start,
          ticket_validity_end: formData.ticket_validity_end,
          dining_plan: formData.dining_plan,
          memory_maker: formData.memory_maker,
          ground_transportation: formData.ground_transportation,
          travel_protection: formData.travel_protection,
          travel_protection_price: formData.travel_protection_price,
          total_price: formData.total_price,
          total_price_due_by: formData.total_price_due_by,
          deposit_due: formData.deposit_due,
          deposit_due_by: formData.deposit_due_by,
          special_requests: formData.special_requests,
          children: formData.children
        }
      };
      console.log('Calling onSave with:', componentData);
      onSave(componentData);
    } else {
      console.log('Form validation failed with errors:', errors);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="component-form disney-package-form">
      <div className="form-section">
        <h4>Package Details</h4>
        <div className="form-group">
          <label>Package Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., Disney World Resort Package"
            className="form-control"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Resort Accommodation</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Check-in Date *</label>
            <input
              type="date"
              name="check_in_date"
              value={formData.check_in_date}
              onChange={handleInputChange}
              className={`form-control ${errors.check_in_date ? 'error' : ''}`}
            />
            {errors.check_in_date && <span className="error-text">{errors.check_in_date}</span>}
          </div>
          <div className="form-group">
            <label>Check-out Date *</label>
            <input
              type="date"
              name="check_out_date"
              value={formData.check_out_date}
              onChange={handleInputChange}
              className={`form-control ${errors.check_out_date ? 'error' : ''}`}
            />
            {errors.check_out_date && <span className="error-text">{errors.check_out_date}</span>}
          </div>
        </div>
        
        <div className="form-group">
          <label>Resort Name *</label>
          <select
            name="resort_name"
            value={formData.resort_name}
            onChange={handleInputChange}
            className={`form-control ${errors.resort_name ? 'error' : ''}`}
          >
            <option value="">Select a Resort</option>
            {resortOptions.map(resort => (
              <option key={resort} value={resort}>{resort}</option>
            ))}
          </select>
          {errors.resort_name && <span className="error-text">{errors.resort_name}</span>}
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Room Type</label>
            <input
              type="text"
              name="room_type"
              value={formData.room_type}
              onChange={handleInputChange}
              placeholder="e.g., Standard Room, Suite"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>View</label>
            <input
              type="text"
              name="view"
              value={formData.view}
              onChange={handleInputChange}
              placeholder="e.g., Resort View, Pool View"
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Theme Park Tickets</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Ticket Type</label>
            <select
              name="ticket_type"
              value={formData.ticket_type}
              onChange={handleInputChange}
              className="form-control"
            >
              {ticketTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Ticket Options</label>
            <select
              name="ticket_options"
              value={formData.ticket_options}
              onChange={handleInputChange}
              className="form-control"
            >
              {ticketOptionsChoices.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Ticket Validity Start</label>
            <input
              type="date"
              name="ticket_validity_start"
              value={formData.ticket_validity_start}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Ticket Validity End</label>
            <input
              type="date"
              name="ticket_validity_end"
              value={formData.ticket_validity_end}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Add-on Services</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Dining Plan</label>
            <select
              name="dining_plan"
              value={formData.dining_plan}
              onChange={handleInputChange}
              className="form-control"
            >
              {diningPlanOptions.map(plan => (
                <option key={plan} value={plan}>{plan}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Memory Maker</label>
            <select
              name="memory_maker"
              value={formData.memory_maker}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label>Ground Transportation</label>
          <input
            type="text"
            name="ground_transportation"
            value={formData.ground_transportation}
            onChange={handleInputChange}
            placeholder="Transportation details"
            className="form-control"
          />
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Travel Protection</label>
            <select
              name="travel_protection"
              value={formData.travel_protection}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="Not Included">Not Included</option>
              <option value="Included">Included</option>
            </select>
          </div>
          {formData.travel_protection === 'Included' && (
            <div className="form-group">
              <label>Travel Protection Price ($)</label>
              <input
                type="number"
                name="travel_protection_price"
                value={formData.travel_protection_price}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                className="form-control"
              />
            </div>
          )}
        </div>
      </div>

      <div className="form-section">
        <h4>Pricing & Payment</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Total Price ($) *</label>
            <input
              type="number"
              name="total_price"
              value={formData.total_price}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`form-control ${errors.total_price ? 'error' : ''}`}
            />
            {errors.total_price && <span className="error-text">{errors.total_price}</span>}
          </div>
          <div className="form-group">
            <label>Total Price Due By</label>
            <input
              type="date"
              name="total_price_due_by"
              value={formData.total_price_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Deposit Due ($) *</label>
            <input
              type="number"
              name="deposit_due"
              value={formData.deposit_due}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`form-control ${errors.deposit_due ? 'error' : ''}`}
            />
            {errors.deposit_due && <span className="error-text">{errors.deposit_due}</span>}
          </div>
          <div className="form-group">
            <label>Deposit Due By</label>
            <input
              type="date"
              name="deposit_due_by"
              value={formData.deposit_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Additional Information</h4>
        <div className="form-group">
          <label>Special Requests</label>
          <textarea
            name="special_requests"
            value={formData.special_requests}
            onChange={handleInputChange}
            rows="3"
            className="form-control"
            placeholder="Any special requests or preferences for this Disney package"
          />
        </div>
      </div>

      <CommonOptionFields formData={formData} setFormData={setFormData} />

      <AttachmentUpload
        photos={formData.photos || []}
        documents={formData.documents || []}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Add to Quote
        </button>
      </div>
    </form>
  );
};

export default DisneyThemeParkPackageForm;
